import React, { useState, useEffect } from 'react';
import { csrfFetch } from '../../utils/csrfFetch';

/**
 * ShareDialog - Modal for sharing a conversation via link
 * Features:
 * - Generate/display share link
 * - Copy link to clipboard
 * - Revoke share access
 * - Show view count
 */
function ShareDialog({ isOpen, onClose, conversationId, conversationTitle }) {
  const [shareInfo, setShareInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Fetch share status when dialog opens
  useEffect(() => {
    if (isOpen && conversationId) {
      fetchShareStatus();
    }
  }, [isOpen, conversationId]);

  const fetchShareStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await csrfFetch(`/api/conversations/${conversationId}/share`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setShareInfo(data);
      } else {
        throw new Error('Failed to fetch share status');
      }
    } catch (err) {
      console.error('Error fetching share status:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateShare = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await csrfFetch(`/api/conversations/${conversationId}/share`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setShareInfo({
          isShared: true,
          shareToken: data.shareToken,
          shareUrl: data.shareUrl,
          createdAt: data.createdAt,
          viewCount: data.viewCount
        });
      } else {
        throw new Error('Failed to create share link');
      }
    } catch (err) {
      console.error('Error creating share:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeShare = async () => {
    if (!confirm('Are you sure you want to revoke this share link? Anyone with the link will no longer be able to access this conversation.')) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await csrfFetch(`/api/conversations/${conversationId}/share`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setShareInfo({ isShared: false, shareUrl: null });
      } else {
        throw new Error('Failed to revoke share link');
      }
    } catch (err) {
      console.error('Error revoking share:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareInfo?.shareUrl) return;

    const fullUrl = `${window.location.origin}${shareInfo.shareUrl}`;

    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  const fullShareUrl = shareInfo?.shareUrl
    ? `${window.location.origin}${shareInfo.shareUrl}`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-xs"
        onClick={onClose}
      />

      {/* Dialog */}
      <div role="dialog" aria-modal="true" aria-label="Share Conversation" className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Share Conversation
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[250px]">
                {conversationTitle || 'Untitled Conversation'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {isLoading && !shareInfo ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-decisiv-blue"></div>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-red-500 dark:text-red-400">{error}</p>
              <button
                onClick={fetchShareStatus}
                className="mt-3 text-sm text-decisiv-blue hover:underline"
              >
                Try again
              </button>
            </div>
          ) : shareInfo?.isShared ? (
            <div className="space-y-4">
              {/* Share link display */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Share Link
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={fullShareUrl}
                    className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 truncate"
                  />
                  <button
                    onClick={handleCopyLink}
                    disabled={isLoading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      copied
                        ? 'bg-green-500 text-white'
                        : 'bg-decisiv-blue text-white hover:bg-opacity-90'
                    }`}
                  >
                    {copied ? (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied!
                      </span>
                    ) : (
                      'Copy'
                    )}
                  </button>
                </div>
              </div>

              {/* Share info */}
              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {shareInfo.viewCount || 0} views
                </span>
                <span>
                  Shared {new Date(shareInfo.createdAt).toLocaleDateString()}
                </span>
              </div>

              {/* Info notice */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Read-only:</strong> Anyone with this link can view this conversation but cannot edit or reply.
                </p>
              </div>

              {/* Revoke button */}
              <button
                onClick={handleRevokeShare}
                disabled={isLoading}
                className="w-full px-4 py-2 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {isLoading ? 'Revoking...' : 'Revoke Share Link'}
              </button>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Share this conversation
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Create a public link to share this conversation with anyone. They'll be able to view but not edit.
              </p>
              <button
                onClick={handleCreateShare}
                disabled={isLoading}
                className="px-6 py-2.5 bg-decisiv-blue text-white rounded-lg hover:bg-opacity-90 transition-colors font-medium disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </span>
                ) : (
                  'Create Share Link'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ShareDialog;
